import { useEffect, useMemo, useRef, useState } from "react";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2, Clock, X, RotateCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const fmt = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));
const REPLACE_TOKEN = "REEMPLAZAR";

const PHASE_LABEL = {
  queued:    "En cola…",
  uploading: "Subiendo archivo…",
  parsing:   "Leyendo hoja Export…",
  purging:   "Vaciando datos previos…",
  upserting: "Insertando/actualizando…",
  done:      "Listo",
};

/**
 * Generic async XLSX upload card with:
 *  - Client-side file picker (validates .xlsx / .csv).
 *  - "Append" vs "Replace" mode radio (opt-in via `showModeSelector`).
 *  - Background polling of upload_jobs, resumable on page reload.
 *  - Progress strip and result banner.
 *  - Last 5 uploads history.
 *
 * Props:
 *  - kind:        "sales" | "inventory" — used for storage key + job filter.
 *  - endpoint:    absolute API path, e.g. "/admin/sales/upload-xlsx".
 *  - title:       Card title (e.g. "Facturación (Ventas)").
 *  - description: Small helper text below title.
 *  - resultKeys:  {
 *      insertedLabel:  "Insertadas" | "Nuevas",
 *      updatedLabel:   "Actualizadas",
 *      totalKey:       "total_sales_after" | "total_inventory_after",
 *      totalLabel:     "Total en DB" | "Total en inventario",
 *    }
 *  - showModeSelector: boolean — render the Append/Replace radio.
 *  - defaultMode: "append" | "replace" — initial selection.
 *  - onUploaded: optional callback fired after successful upload.
 */
export default function XlsxUploadCard({
  kind,
  endpoint,
  title,
  description,
  resultKeys = {
    insertedLabel: "Insertadas",
    updatedLabel: "Actualizadas",
    totalKey: "total_sales_after",
    totalLabel: "Total en DB",
  },
  showModeSelector = true,
  defaultMode = "append",
  testid = "xlsx-upload-card",
  onUploaded,
  maxDays = 14,
}) {
  const STORAGE_KEY = `xlsx_upload_active_job_${kind}`;

  const [file, setFile] = useState(null);
  const [mode, setMode] = useState(defaultMode);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [replaceConfirm, setReplaceConfirm] = useState({ open: false, token: "" });
  const cancelRef = useRef(false);

  const ACCEPT = ".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";

  const handleFile = (f) => {
    setFile(f);
    setResult(null);
    setError(null);
  };

  const loadHistory = () => {
    setHistoryLoading(true);
    api.get(`/upload-jobs?kind=${kind}&limit=5`)
      .then((r) => setHistory(r.data?.items || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  };

  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(loadHistory, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [busy]);
  useEffect(() => {
    if (result || error) loadHistory();
    // eslint-disable-next-line
  }, [result, error]);

  // Resume polling for any active upload after a page reload.
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;
    try {
      const { jobId, fileName } = JSON.parse(saved);
      if (!jobId) return;
      api.get(`/upload-jobs/${jobId}`).then((r) => {
        const d = r.data;
        if (d.status === "running" || d.status === "queued") {
          setBusy(true);
          setJob({ phase: d.phase, current: d.progress_current || 0, total: d.progress_total || 0 });
          const toastId = toast.loading(`Reanudando seguimiento de ${fileName}…`);
          pollJob(jobId, toastId, fileName);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      }).catch(() => localStorage.removeItem(STORAGE_KEY));
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
    // eslint-disable-next-line
  }, []);

  const pollJob = async (jobId, toastId, fileName) => {
    while (!cancelRef.current) {
      try {
        const r = await api.get(`/upload-jobs/${jobId}`);
        const d = r.data;
        setJob({ phase: d.phase, current: d.progress_current || 0, total: d.progress_total || 0 });
        const lbl = PHASE_LABEL[d.phase] || `Fase: ${d.phase || "…"}`;
        toast.loading(`${lbl} · ${fileName}`, { id: toastId });

        if (d.status === "done") {
          localStorage.removeItem(STORAGE_KEY);
          const res = d.result || {};
          if (res.ok === false) {
            const msg = res.error || "El archivo no contiene filas válidas";
            setError(msg);
            toast.error("No se procesó ninguna fila", { id: toastId, description: msg });
          } else {
            setResult(res);
            toast.success("Carga completada", {
              id: toastId,
              description: `${fmt(res.rows_parsed)} filas · ${fmt(res.upserted)} insertadas · ${fmt(res.modified)} actualizadas`,
            });
            if (onUploaded) onUploaded(res);
          }
          setBusy(false);
          setJob(null);
          return;
        }
        if (d.status === "error") {
          localStorage.removeItem(STORAGE_KEY);
          const msg = d.error || "Error en el procesamiento";
          setError(msg);
          toast.error(`Error procesando ${title}`, { id: toastId, description: msg });
          setBusy(false);
          setJob(null);
          return;
        }
      } catch (_) { /* transient */ }
      await new Promise((res) => setTimeout(res, 2000));
    }
  };

  const upload = async () => {
    if (!file) {
      toast.warning("Selecciona un archivo primero");
      return;
    }
    const fileName = file.name;
    cancelRef.current = false;
    setBusy(true);
    setError(null);
    setResult(null);
    setJob({ phase: "uploading", current: 0, total: 0 });
    const toastId = toast.loading(`Subiendo ${fileName}…`);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const qs = `async_job=true&mode=${mode}`;
      const r = await api.post(`${endpoint}?${qs}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      if (r.data?.job_id) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ jobId: r.data.job_id, fileName }));
        toast.loading(`Procesando ${fileName} en segundo plano…`, { id: toastId });
        await pollJob(r.data.job_id, toastId, fileName);
      } else if (r.data?.ok) {
        setResult(r.data);
        toast.success("Carga completada", { id: toastId,
          description: `${fmt(r.data.rows_parsed)} filas procesadas` });
        if (onUploaded) onUploaded(r.data);
        setBusy(false);
        setJob(null);
      } else {
        const msg = r.data?.error || "Respuesta inesperada del servidor";
        setError(msg);
        toast.error("No se procesó ninguna fila", { id: toastId, description: msg });
        setBusy(false);
        setJob(null);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Error subiendo archivo";
      setError(msg);
      toast.error(`Error al subir ${title}`, { id: toastId, description: msg });
      setBusy(false);
      setJob(null);
    }
  };

  const phase = job?.phase;
  const pct = job?.total > 0 ? Math.min(100, (job.current / job.total) * 100) : null;

  // Derive the last successful upload from the history list — used to render
  // the prominent "Última actualización" stat at the top of every card.
  const lastSuccessful = useMemo(
    () => (history || []).find((j) => j.status === "done") || null,
    [history],
  );

  return (
    <div className="surface p-5" data-testid={testid}>
      <div className="flex items-start gap-3 mb-3">
        <div className="rounded-lg p-2" style={{ background: "#fef3c7" }}>
          <FileSpreadsheet size={20} style={{ color: "#92400e" }}/>
        </div>
        <div className="flex-1">
          <h3 className="font-display font-extrabold text-base">{title}</h3>
          <p className="text-xs text-gray-500 font-semibold mt-1">{description}</p>
        </div>
      </div>

      <LastUpdateStat job={lastSuccessful} loading={historyLoading} testid={testid} maxDays={maxDays}/>

      {/* Mode selector (Append / Replace) */}
      {showModeSelector && (
        <div className="grid gap-2 mb-3" data-testid={`${testid}-mode`}>
          <ModeOption
            checked={mode === "append"}
            onChange={() => setMode("append")}
            disabled={busy}
            title="Añadir / Actualizar (append)"
            desc="Inserta nuevos y actualiza existentes por clave única. Nada se elimina. Recomendado para cargas incrementales."
            testid={`${testid}-mode-append`}
          />
          <ModeOption
            checked={mode === "replace"}
            onChange={() => setMode("replace")}
            disabled={busy}
            title="Reemplazar todos los datos"
            desc="El XLSX es la fuente completa. Registros que NO estén en el archivo se eliminan. Los datos previos se borran al iniciar (no antes)."
            testid={`${testid}-mode-replace`}
          />
        </div>
      )}

      <div
        className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center mb-3 transition-colors hover:border-amber-400"
        style={{ background: file ? "#fffbeb" : "#fafafa" }}
      >
        <input
          type="file"
          accept={ACCEPT}
          onChange={(e) => handleFile(e.target.files?.[0] || null)}
          className="hidden"
          id={`${testid}-input`}
          data-testid={`${testid}-input`}
          disabled={busy}
        />
        <label htmlFor={`${testid}-input`} className={`cursor-pointer ${busy ? "opacity-50" : ""}`}>
          {file ? (
            <div className="flex items-center justify-center gap-2">
              <FileSpreadsheet size={18}/>
              <span className="font-bold text-sm">{file.name}</span>
              <span className="text-xs text-gray-500">({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
              {!busy && !result && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setFile(null);
                    setResult(null);
                    setError(null);
                    const input = document.getElementById(`${testid}-input`);
                    if (input) input.value = "";
                  }}
                  className="rounded-full p-1 hover:bg-red-100 transition"
                  style={{ color: "#dc2626", lineHeight: 0 }}
                  title="Quitar archivo"
                  data-testid={`${testid}-remove-file`}
                ><X size={14}/></button>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 text-sm font-semibold text-gray-600">
              <Upload size={16}/> Click para seleccionar archivo .xlsx o .csv
            </div>
          )}
        </label>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => {
            if (!file) {
              toast.warning("Selecciona un archivo primero");
              return;
            }
            if (mode === "replace") {
              setReplaceConfirm({ open: true, token: "" });
              return;
            }
            upload();
          }}
          disabled={!file || busy}
          className="px-5 py-2 rounded-lg font-extrabold text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: "#111827", color: "#f4c400" }}
          data-testid={`${testid}-submit`}
        >
          {busy
            ? (<><Loader2 size={14} className="animate-spin"/> {PHASE_LABEL[phase] || "Procesando…"}</>)
            : (<><Upload size={14}/> {mode === "replace" ? "Subir y reemplazar" : "Subir y procesar"}</>)}
        </button>
      </div>

      {busy && (
        <div className="mt-3 p-3 rounded-lg border flex items-start gap-2"
             style={{ background: "#eff6ff", borderColor: "#bfdbfe" }}
             data-testid={`${testid}-progress`}>
          <Loader2 size={18} className="animate-spin" style={{ color: "#1d4ed8", flexShrink: 0, marginTop: 2 }}/>
          <div className="flex-1">
            <div className="flex items-center justify-between text-sm">
              <span className="font-bold" style={{ color: "#1e3a8a" }}>{PHASE_LABEL[phase] || "Iniciando…"}</span>
              {pct !== null && (
                <span className="font-mono text-xs font-bold" style={{ color: "#1e3a8a" }}>
                  {fmt(job.current)} / {fmt(job.total)} · {pct.toFixed(0)}%
                </span>
              )}
            </div>
            <div className="text-xs font-semibold mt-1" style={{ color: "#1e3a8a" }}>
              El proceso corre en segundo plano. Puedes cerrar esta ventana — el upload no se detendrá.
            </div>
            <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "#dbeafe" }}>
              <div className="h-full" style={{
                width: pct !== null ? `${pct}%` : "20%",
                background: "linear-gradient(90deg, #3b82f6, #2563eb)",
                transition: "width 0.4s ease",
                animation: pct === null ? "indeterminate 1.6s ease-in-out infinite" : "none",
              }}/>
            </div>
          </div>
        </div>
      )}

      {result && result.ok && (
        <div className="mt-3 p-3 rounded-lg flex items-start gap-2"
             style={{ background: "#dcfce7", border: "1px solid #86efac" }}
             data-testid={`${testid}-result`}>
          <CheckCircle2 size={18} style={{ color: "#166534", flexShrink: 0, marginTop: 2 }}/>
          <div className="text-sm">
            <div className="font-bold" style={{ color: "#166534" }}>
              {fmt(result.rows_parsed)} filas procesadas
              {result.mode && <span className="ml-2 text-xs font-extrabold" style={{ color: result.mode === "replace" ? "#b91c1c" : "#166534" }}>
                · modo {result.mode.toUpperCase()}
              </span>}
            </div>
            <div className="text-xs text-gray-700 mt-1">
              · {resultKeys.insertedLabel}: <strong>{fmt(result.upserted)}</strong>
              · {resultKeys.updatedLabel}: <strong>{fmt(result.modified)}</strong>
              · {resultKeys.totalLabel}: <strong>{fmt(result[resultKeys.totalKey])}</strong>
              {result.available_after !== undefined && (
                <>  · Disponibles: <strong>{fmt(result.available_after)}</strong></>
              )}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 p-3 rounded-lg flex items-start gap-2"
             style={{ background: "#fee2e2", border: "1px solid #fca5a5" }}
             data-testid={`${testid}-error`}>
          <AlertCircle size={18} style={{ color: "#991b1b", flexShrink: 0, marginTop: 2 }}/>
          <div className="text-sm font-bold" style={{ color: "#991b1b" }}>{error}</div>
        </div>
      )}

      <UploadHistory items={history} loading={historyLoading} onRefresh={loadHistory} testid={testid}/>

      {replaceConfirm.open && (
        <ReplaceConfirmModal
          testid={testid}
          title={title}
          file={file}
          lastSuccessful={lastSuccessful}
          resultKeys={resultKeys}
          token={replaceConfirm.token}
          onTokenChange={(v) => setReplaceConfirm((s) => ({ ...s, token: v }))}
          onCancel={() => setReplaceConfirm({ open: false, token: "" })}
          onConfirm={() => {
            setReplaceConfirm({ open: false, token: "" });
            upload();
          }}
        />
      )}
    </div>
  );
}

/**
 * Modal de confirmación para modo REPLACE.
 *
 * Motivación: en agosto/2026 un partial-REPLACE del uploader wipeó la
 * colección `sales` de producción (52K → 6 filas de julio). El modo
 * REPLACE ya venía con un radio y descripción, pero el usuario aún lo
 * seleccionó por accidente y clickeó Subir sin releer.
 *
 * Guardrails:
 *  - Muestra tamaño del archivo (una pista fuerte cuando el archivo es
 *    sospechosamente pequeño para el dataset esperado).
 *  - Muestra la última carga OK (fecha + filas) para comparar.
 *  - Exige escribir el token literal "REEMPLAZAR" — imposible de aceptar
 *    haciendo click accidental o pulsando Enter.
 */
function ReplaceConfirmModal({
  testid, title, file, lastSuccessful, resultKeys, token, onTokenChange, onCancel, onConfirm,
}) {
  const canProceed = token.trim().toUpperCase() === REPLACE_TOKEN;
  const lastRows = lastSuccessful?.result?.rows_parsed
    || lastSuccessful?.result?.total
    || lastSuccessful?.result?.upserted
    || 0;
  const lastWhen = lastSuccessful?.finished_at || lastSuccessful?.updated_at || lastSuccessful?.created_at;
  const fileMB = file ? file.size / 1024 / 1024 : 0;
  // Heurística simple: <2 MB para facturación/inventario es muy poco.
  // No bloquea — solo advierte más fuerte visualmente.
  const smallFileWarning = fileMB > 0 && fileMB < 2;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15, 23, 42, 0.75)" }}
      onClick={onCancel}
      data-testid={`${testid}-replace-modal`}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header rojo destructivo */}
        <div className="px-6 py-4" style={{ background: "#dc2626", color: "#fff" }}>
          <div className="flex items-center gap-3">
            <ShieldAlert size={26}/>
            <div>
              <div className="font-display font-extrabold text-lg leading-tight">
                Confirmar reemplazo total
              </div>
              <div className="text-xs font-semibold opacity-90 mt-0.5">
                {title} · modo REPLACE
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="text-sm text-gray-800 leading-relaxed">
            Vas a <strong className="text-red-700">borrar toda la data actual</strong> de <em>{title}</em>{" "}
            y reemplazarla por el contenido del archivo subido. Los registros que{" "}
            <strong>no estén</strong> en el archivo se pierden.
          </div>

          {/* Comparativa: archivo actual vs última carga OK */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg border" style={{
              background: smallFileWarning ? "#fef2f2" : "#f8fafc",
              borderColor: smallFileWarning ? "#fecaca" : "#e2e8f0",
            }}>
              <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 mb-1">
                Archivo a subir
              </div>
              <div className="text-sm font-extrabold text-gray-900 truncate" title={file?.name}>
                {file?.name || "—"}
              </div>
              <div className="text-xs font-bold mt-1" style={{
                color: smallFileWarning ? "#991b1b" : "#475569",
              }}>
                {fileMB.toFixed(2)} MB {smallFileWarning && "⚠ pequeño"}
              </div>
            </div>
            <div className="p-3 rounded-lg border" style={{
              background: "#f0fdf4", borderColor: "#bbf7d0",
            }}>
              <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 mb-1">
                Última carga OK
              </div>
              <div className="text-sm font-extrabold text-gray-900">
                {lastRows ? `${fmt(lastRows)} filas` : "—"}
              </div>
              <div className="text-xs font-bold text-gray-500 mt-1">
                {lastWhen
                  ? new Date(lastWhen).toLocaleString("es-DO", { dateStyle: "short", timeStyle: "short" })
                  : "sin cargas previas"}
              </div>
            </div>
          </div>

          {smallFileWarning && (
            <div className="flex items-start gap-2 p-3 rounded-lg" style={{
              background: "#fef2f2", border: "1px solid #fecaca",
            }}>
              <AlertCircle size={14} style={{ color: "#991b1b", flexShrink: 0, marginTop: 2 }}/>
              <div className="text-xs font-semibold" style={{ color: "#7f1d1d" }}>
                El archivo pesa <strong>{fileMB.toFixed(2)} MB</strong>. Si esto es una
                exportación mensual parcial, es probable que <u>no contenga toda la historia</u>{" "}
                — un REPLACE con este archivo eliminaría el resto de la data. Considera
                usar <strong>Añadir/Actualizar</strong> en su lugar.
              </div>
            </div>
          )}

          {/* Token de confirmación */}
          <div>
            <label className="block text-[11px] font-extrabold text-gray-500 uppercase tracking-wider mb-2">
              Escribe <span className="text-red-600">{REPLACE_TOKEN}</span> para confirmar
            </label>
            <input
              type="text"
              value={token}
              onChange={(e) => onTokenChange(e.target.value)}
              placeholder={REPLACE_TOKEN}
              autoComplete="off"
              autoFocus
              data-testid={`${testid}-replace-confirm-input`}
              className="w-full text-sm px-3 py-2 rounded-lg border font-extrabold tracking-widest"
              style={{ borderColor: "#cbd5e1", background: "#fff" }}
            />
          </div>
        </div>

        {/* Footer con acciones */}
        <div className="px-6 py-4 flex items-center justify-end gap-2" style={{
          background: "#f8fafc", borderTop: "1px solid #e2e8f0",
        }}>
          <button
            type="button"
            onClick={onCancel}
            data-testid={`${testid}-replace-cancel`}
            className="px-4 py-2 rounded-lg text-sm font-extrabold text-gray-700 hover:bg-gray-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canProceed}
            data-testid={`${testid}-replace-confirm`}
            className="px-5 py-2 rounded-lg text-sm font-extrabold disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: canProceed ? "#dc2626" : "#e5e7eb",
              color: canProceed ? "#fff" : "#9ca3af",
              boxShadow: canProceed ? "0 4px 12px rgba(220, 38, 38, 0.35)" : "none",
            }}
          >
            Sí, reemplazar todo
          </button>
        </div>
      </div>
    </div>
  );
}



function ModeOption({ checked, onChange, disabled, title, desc, testid }) {
  return (
    <label
      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
        checked ? "border-amber-400 bg-amber-50" : "border-gray-200 bg-white hover:border-gray-300"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      data-testid={testid}
    >
      <input type="radio" checked={checked} onChange={onChange} disabled={disabled} className="mt-1 accent-amber-500"/>
      <div>
        <div className="text-sm font-extrabold" style={{ color: "#111827" }}>{title}</div>
        <div className="text-xs text-gray-500 font-semibold mt-0.5">{desc}</div>
      </div>
    </label>
  );
}

function UploadHistory({ items, loading, onRefresh, testid }) {
  return (
    <div className="mt-5 pt-4 border-t border-gray-100" data-testid={`${testid}-history`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-extrabold uppercase tracking-wider text-gray-500">Últimas cargas</h4>
        <button onClick={onRefresh} className="text-[11px] font-extrabold text-gray-500 hover:text-gray-900"
                data-testid={`${testid}-history-refresh`}>Refrescar</button>
      </div>
      {loading && items.length === 0 ? (
        <div className="text-xs font-semibold text-gray-400 py-3 text-center">Cargando historial…</div>
      ) : items.length === 0 ? (
        <div className="text-xs font-semibold text-gray-400 py-3 text-center">No hay cargas previas.</div>
      ) : (
        <div className="space-y-1.5">
          {items.map((it) => <UploadHistoryRow key={it._id} item={it}/>)}
        </div>
      )}
    </div>
  );
}

function UploadHistoryRow({ item }) {
  const fmtN = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));
  const fmtDate = (s) => s ? new Date(s).toLocaleString("es-DO", { dateStyle: "short", timeStyle: "short" }) : "—";
  const STATUS = {
    queued:  { color: "#6b7280", bg: "#f3f4f6", label: "En cola",   icon: <Clock size={12}/> },
    running: { color: "#1d4ed8", bg: "#dbeafe", label: "Procesando", icon: <Loader2 size={12} className="animate-spin"/> },
    done:    { color: "#166534", bg: "#dcfce7", label: "OK",        icon: <CheckCircle2 size={12}/> },
    error:   { color: "#991b1b", bg: "#fee2e2", label: "Error",     icon: <AlertCircle size={12}/> },
  };
  const st = STATUS[item.status] || STATUS.queued;
  const rows = item.result?.rows_parsed;
  const upserted = item.result?.upserted;
  const modeTag = item.replace ? "REPLACE" : "APPEND";
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg" style={{ background: "#fafafa" }}
         data-testid={`history-row-${item._id}`}>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold flex-shrink-0"
            style={{ background: st.bg, color: st.color }}>
        {st.icon}{st.label}
      </span>
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-extrabold flex-shrink-0"
            style={{ background: item.replace ? "#fee2e2" : "#e0e7ff", color: item.replace ? "#991b1b" : "#3730a3" }}>
        {modeTag}
      </span>
      <span className="font-display font-extrabold text-xs truncate flex-1 min-w-0" title={item.filename}>{item.filename}</span>
      {item.status === "done" && rows != null && (
        <span className="text-[11px] font-bold text-gray-600 flex-shrink-0" style={{ fontVariantNumeric: "tabular-nums" }}>
          {fmtN(rows)} filas{upserted != null ? ` · ${fmtN(upserted)} ins.` : ""}
        </span>
      )}
      {item.status === "running" && item.progress_total > 0 && (
        <span className="text-[11px] font-bold text-blue-700 flex-shrink-0" style={{ fontVariantNumeric: "tabular-nums" }}>
          {fmtN(item.progress_current)} / {fmtN(item.progress_total)}
        </span>
      )}
      {item.status === "error" && (
        <span className="text-[11px] font-bold text-red-700 flex-shrink-0 truncate" title={item.error || ""}>
          {(item.error || "Error desconocido").slice(0, 40)}
        </span>
      )}
      <span className="text-[10px] font-bold text-gray-400 flex-shrink-0">{fmtDate(item.created_at)}</span>
    </div>
  );
}


/**
 * Prominent "Última actualización" stat rendered at the top of every upload
 * card. Uses the most recent DONE job from the history. Shows a friendly
 * relative time ("hace 2 h") + full timestamp on hover.
 */
export function LastUpdateStat({ job, loading, testid, maxDays = 14 }) {
  if (loading && !job) {
    return (
      <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-gray-400"
           style={{ background: "#fafbfc", border: "1px solid #e5e7eb" }}>
        <Loader2 size={12} className="animate-spin"/> Cargando historial…
      </div>
    );
  }
  const fmtFull = (s) => s
    ? new Date(s).toLocaleString("es-DO", { dateStyle: "medium", timeStyle: "short" })
    : "—";
  const fmtRel = (s) => {
    if (!s) return "Nunca";
    const diff = Date.now() - new Date(s).getTime();
    if (diff < 0) return "en un momento";
    const min  = Math.floor(diff / 60000);
    const hr   = Math.floor(min / 60);
    const day  = Math.floor(hr / 24);
    if (min < 1)   return "hace instantes";
    if (min < 60)  return `hace ${min} min`;
    if (hr < 24)   return `hace ${hr} h`;
    if (day < 30)  return `hace ${day} d`;
    return new Date(s).toLocaleDateString("es-DO", { day: "numeric", month: "short", year: "numeric" });
  };
  if (!job) {
    return (
      <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold"
           style={{ background: "#fef3c7", border: "1px solid #fde68a", color: "#92400e" }}
           data-testid={`${testid}-last-update-empty`}>
        <Clock size={13}/> Sin cargas previas. Sube el primer archivo para empezar.
      </div>
    );
  }
  const finishedAt = job.finished_at || job.updated_at || job.created_at;
  const rows = job.result?.rows_parsed || job.result?.total || job.result?.upserted;

  // Stale-data warning: if the last successful upload is older than the
  // per-card threshold (7 days for leads, 14 for sales/inventory), render
  // the banner in yellow with a warning icon to nudge a data refresh.
  const ageDays = Math.floor((Date.now() - new Date(finishedAt).getTime()) / 86400000);
  const isStale = ageDays > maxDays;

  if (isStale) {
    return (
      <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
           style={{ background: "#fef9c3", border: "1px solid #fde047" }}
           title={fmtFull(finishedAt)}
           data-testid={`${testid}-last-update-stale`}>
        <AlertCircle size={13} style={{ color: "#a16207", flexShrink: 0 }}/>
        <div className="flex-1 flex items-center gap-2 flex-wrap">
          <span className="font-extrabold" style={{ color: "#854d0e" }}>
            Datos con {ageDays} días · considera refrescar
          </span>
          <span className="text-[11px] font-semibold hidden sm:inline" style={{ color: "#a16207" }}>
            · última carga {fmtRel(finishedAt)}
          </span>
          {rows > 0 && (
            <span className="pill text-[10px] font-extrabold" style={{
              background: "#a16207", color: "#fff", padding: "1px 8px",
            }}>
              {fmt(rows)} filas
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
         style={{ background: "#dcfce7", border: "1px solid #86efac" }}
         title={fmtFull(finishedAt)}
         data-testid={`${testid}-last-update`}>
      <RotateCw size={13} style={{ color: "#166534", flexShrink: 0 }}/>
      <div className="flex-1 flex items-center gap-2 flex-wrap">
        <span className="font-extrabold" style={{ color: "#166534" }}>
          Última actualización: {fmtRel(finishedAt)}
        </span>
        <span className="text-[11px] font-semibold text-gray-600 hidden sm:inline">
          · {new Date(finishedAt).toLocaleString("es-DO", { dateStyle: "short", timeStyle: "short" })}
        </span>
        {rows > 0 && (
          <span className="pill text-[10px] font-extrabold" style={{
            background: "#166534", color: "#fff", padding: "1px 8px",
          }}>
            {fmt(rows)} filas
          </span>
        )}
      </div>
    </div>
  );
}
